import React from "react";
import { groupByDate } from "../lib/groupByDate";
import { Journey, Labels, Languages, Status } from "../types";
import { format, parseISO, subHours, isSameDay, isAfter, Locale } from "date-fns";
import { JourneyTime } from "./JourneyTime";
import clsx from "clsx";
import { enUS, ro, uk, tr } from 'date-fns/locale'

export const JourneyList = ({ journeys, labels, lang }: { journeys: Journey[], labels: Labels, lang: Languages }) => {
  const journeysByDate = groupByDate(journeys);

  const locales: Record<Languages, Locale> = {
    en: enUS,
    tr: tr,
    ro: ro,
    ua: uk,
  };

  const statuses = {
    departed: labels.departed,
    delayed: labels.delayed,
    arrived: labels.arrived,
    cancelled: labels.cancelled,
  };

  return (
    <table className="live-timetable__table">
      <thead>
        <tr>
          <th>{labels.cutOff}</th>
          <th>{labels.departure}</th>
          <th>{labels.arrival}</th>
          <th>{labels.status}</th>
        </tr>
      </thead>

      <tbody>
        {Object.entries(journeysByDate).map(([date, journeys]) => {
          const dateHeading = format(parseISO(date), "EEEE dd-MM-yyyy", {locale: locales[lang]});

          return (<React.Fragment key={date}>
            <tr className="live-timetable__date-heading">
              <th colSpan={4}>{dateHeading.charAt(0).toUpperCase() + dateHeading.slice(1)}</th>
            </tr> 

            {journeys.map((journey, index) => {
              const cutOff = parseISO(journey.cutOff);
              const departure = parseISO(journey.departure);
              const estimatedDeparture = journey.estimatedDeparture
                ? parseISO(journey.estimatedDeparture)
                : null;

              const arrival = parseISO(journey.arrival);
              const estimatedArrival = journey.estimatedArrival
                ? parseISO(journey.estimatedArrival)
                : null;

              let status: Status = "none";

              if (estimatedDeparture && estimatedDeparture > departure) {
                status = "delayed";
              } else if (estimatedArrival && estimatedArrival > arrival) {
                status = "delayed";
              } else if (isAfter(new Date(), estimatedArrival || arrival)) {
                status = "arrived";
              } else if (isAfter(new Date(), estimatedDeparture || departure)) {
                status = "departed";
              } else if (!journey.openForBooking && !journey.openForCheckin) {
                status = "cancelled";
              }

              const departureIsDelayedMoreThanOneDay = estimatedDeparture && !isSameDay(departure, estimatedDeparture);
              const arrivalIsDelayedMoreThanOneDay = estimatedArrival && !isSameDay(arrival, estimatedArrival);
              const arrivalIsNotTheSameDayAsDeparture = !isSameDay(departure, arrival);

              return (
                <React.Fragment key={index}>
                  <tr className={clsx({'live-timetable__cancelled': status === "cancelled"})}>
                    <td data-label={labels.cutOff} data-order="3">
                      {
                        cutOff ? format(cutOff, "HH:mm") : ''
                      }
                    </td>

                    <td data-label={labels.departure}>
                      <JourneyTime
                        expected={departure}
                        estimated={estimatedDeparture}
                      />
                      {departureIsDelayedMoreThanOneDay && 
                        <React.Fragment>**<div className="live-timetable__new-date">**{labels.newDepartureDate}: <u>{format(estimatedDeparture, "EEEE dd.MM HH:mm", {locale: locales[lang]})}</u></div></React.Fragment>
                      }
                    </td>

                    <td data-label={labels.arrival}>
                      <JourneyTime
                        expected={arrival}
                        estimated={estimatedArrival}
                      />
                      {!arrivalIsDelayedMoreThanOneDay && arrivalIsNotTheSameDayAsDeparture && "*"}
                      {arrivalIsDelayedMoreThanOneDay && 
                        <React.Fragment>**<div className="live-timetable__new-date">**{labels.newArrivalDate}: <u>{format(estimatedArrival, "EEEE dd.MM HH:mm", {locale: locales[lang]})}</u></div></React.Fragment>
                      }
                    </td>

                    <td
                      data-label={labels.status}
                      className={clsx({
                        "live-timetable__status--delayed": status === "delayed" || status === "cancelled",
                      })}
                    >
                      {status !== "none" && statuses[status]}
                    </td>
                  </tr>
                  {departureIsDelayedMoreThanOneDay && 
                    <tr className="live-timetable__new-date--desktop">
                      <td colSpan={4}>
                        <div className="live-timetable__new-date">**{labels.newDepartureDate}: <u>{format(estimatedDeparture, "EEEE dd.MM HH:mm", {locale: locales[lang]})}</u></div>
                      </td>
                    </tr>
                  }
                  {arrivalIsDelayedMoreThanOneDay && 
                    <tr className="live-timetable__new-date--desktop">
                      <td colSpan={4}>
                        <div className="live-timetable__new-date">**{labels.newArrivalDate}: <u>{format(estimatedArrival, "EEEE dd.MM HH:mm", {locale: locales[lang]})}</u></div>
                      </td>
                    </tr>
                  }
                </React.Fragment>
              );
            })}
          </React.Fragment>)
        })}

        {Object.entries(journeysByDate).length === 0 && (
          <tr className="live-timetable__empty">
            <td colSpan={4}>{labels.noDeparturesFound}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
